/**
 * gate_labels.ts — collect training labels for the bypass-risk gate.
 *
 * For every length-8 leap the planner chooses, we record a labelled
 * example: (gate features at decision time, did-it-bypass-gold?).
 *
 * The bypass label is defined exactly as in `sequential_recommender_moe.ipynb`
 * §6: y = 1 iff the chosen length-8 leap collected zero gold along its path
 * AND there was gold within sensor radius that was NOT on the path. That
 * captures the specific failure mode the gate is supposed to fix.
 *
 * CRITICAL DISCIPLINE (the "no truth at inference" line we hold):
 *   - At LABEL time we read `env.world.cells` (the painted ground truth).
 *     This is allowed — we're collecting supervision, not acting.
 *   - At INFERENCE time the gate sees only the 6 features in gate.ts, which
 *     are computed from `env.belief` (the agent's internal estimate, built
 *     one noisy tick at a time). The gate never queries world.cells.
 *
 * Both checks (gold on path, gold off-path) use the truth as it stands
 * BEFORE env.step() consumes the gold. The caller is responsible for
 * invoking record() BEFORE stepping the env.
 */

import { World, DIRECTIONS, decodeAction } from './environment';
import { GOLD, SENSOR_RADIUS } from './belief';
import { N_GATE_FEAT } from './gate';

/** Default ring-buffer capacity — comfortably holds ~50 episodes of labels. */
export const DEFAULT_LABEL_CAPACITY = 20_000;

export class BypassLabelBuffer {
  /** (capacity × N_GATE_FEAT) flat row-major feature store. */
  readonly feats: Float64Array;
  /** Per-sample label: 1 if the length-8 leap bypassed gold, 0 otherwise. */
  readonly labels: Uint8Array;
  readonly capacity: number;
  /** Number of valid samples currently in the buffer (≤ capacity). */
  size = 0;
  /** Ring-write head. When size === capacity we start overwriting oldest. */
  private head = 0;

  constructor(capacity: number = DEFAULT_LABEL_CAPACITY) {
    this.capacity = capacity;
    this.feats = new Float64Array(capacity * N_GATE_FEAT);
    this.labels = new Uint8Array(capacity);
  }

  /**
   * Record one labelled example. Call BEFORE env.step(action) — the label
   * check reads world.cells, which the env mutates on gold collection.
   *
   * `featRow` is the 6-feature gate input at decision time (length
   * N_GATE_FEAT). `preR`/`preC` are the agent's pose before the leap.
   * `action` is the chosen length-8 leap.
   */
  record(
    world: World,
    preR: number,
    preC: number,
    action: number,
    featRow: Float64Array,
  ): void {
    const { dir, len } = decodeAction(action);
    const [dr, dc] = DIRECTIONS[dir];
    const grid = world.grid;

    // 1. Was any gold collected on the action's path? (pre-step truth)
    let goldOnPath = 0;
    for (let k = 1; k <= len; k++) {
      const nr = preR + dr * k;
      const nc = preC + dc * k;
      if (nr < 0 || nr >= grid || nc < 0 || nc >= grid) break;
      if (world.cells[nr * grid + nc] === GOLD) goldOnPath++;
    }

    // 2. Was there gold within sensor radius NOT on the path?
    // Build the path's cell set first (small — at most 8 cells).
    const pathCells: number[] = [];
    for (let k = 1; k <= len; k++) {
      const nr = preR + dr * k;
      const nc = preC + dc * k;
      if (nr < 0 || nr >= grid || nc < 0 || nc >= grid) break;
      pathCells.push(nr * grid + nc);
    }
    let goldOffPath = 0;
    for (let sdr = -SENSOR_RADIUS; sdr <= SENSOR_RADIUS; sdr++) {
      for (let sdc = -SENSOR_RADIUS; sdc <= SENSOR_RADIUS; sdc++) {
        const nr = preR + sdr;
        const nc = preC + sdc;
        if (nr < 0 || nr >= grid || nc < 0 || nc >= grid) continue;
        const idx = nr * grid + nc;
        if (world.cells[idx] !== GOLD) continue;
        if (pathCells.indexOf(idx) >= 0) continue;
        goldOffPath++;
      }
    }

    // 3. Label: bypassed iff no gold on path AND gold was available nearby.
    const y = goldOnPath === 0 && goldOffPath > 0 ? 1 : 0;

    // 4. Store.
    const off = this.head * N_GATE_FEAT;
    for (let i = 0; i < N_GATE_FEAT; i++) this.feats[off + i] = featRow[i];
    this.labels[this.head] = y;
    this.head = (this.head + 1) % this.capacity;
    if (this.size < this.capacity) this.size++;
  }

  /** Reset the buffer (called on a fresh run). */
  clear(): void {
    this.size = 0;
    this.head = 0;
  }

  /** Fraction of stored samples labelled positive (bypass). For the HUD. */
  get positiveRate(): number {
    if (this.size === 0) return 0;
    let n = 0;
    for (let k = 0; k < this.size; k++) if (this.labels[k] > 0) n++;
    return n / this.size;
  }
}
